import { toPostgresTimestamp } from '../utils/dateTime/date';

// Utility functions for warranty calculations
const calculateWarrantyStatus = (purchaseDate, warrantyDays) => {
    let purchase = parseInt(purchaseDate) * (parseInt(purchaseDate) < 1e12 ? 1000 : 1);
    purchase = new Date(parseInt(purchase));
    const warrantyEnd = new Date(purchase.getTime() + (warrantyDays * 24 * 60 * 60 * 1000));
    const now = new Date();
    if (now > warrantyEnd) return 'Expired';

    const msRemaining = warrantyEnd - now;
    const daysRemaining = Math.ceil(msRemaining / (1000 * 60 * 60 * 24));

    return daysRemaining <= 10 ? 'Expiring soon' : 'In warranty';
};

const getWarrantyEndDate = (purchaseDate, warrantyDays) => {
    let purchase = parseInt(purchaseDate) * (parseInt(purchaseDate) < 1e12 ? 1000 : 1);
    purchase = new Date(parseInt(purchase));
    const warrantyEnd = new Date(purchase.getTime() + (warrantyDays * 24 * 60 * 60 * 1000));
    return warrantyEnd.toLocaleDateString('en-CA');
};

export const formatProductsData = (
    data,
    productCategories,
    productSubcategories,
    models = [],
) => {
    return data.map((item) => {
        const modelObj = models.find(model => model.value === item.skuId);
        let extendedWarranty = modelObj?.extendedWarranty || 0;
        return {
            productId: item.productId,
            serialNo: item.serialNo,
            skuId: item.skuId,
            name: modelObj?.label,
            rawStatus: item.status,
            warrantyDurationInDays: item.warrantyDurationInDays,
            purchaseDate: toPostgresTimestamp(item.purchaseDate),
            registrationChannel: item.registrationChannel,
            registrationFlow: item.registrationFlow,
            category: productCategories.find(cat => cat.value === item.category)?.label ?? item.category,
            categoryObject: {
                value: item.category,
                label: productCategories.find(cat => cat.value === item.category)?.label ?? item.category,
            },
            subCategory: productSubcategories[item.category]?.find(
                sub => sub.value === item.subCategory
            )?.label ?? item.subCategory,
            subCategoryObject: {
                value: item.subCategory,
                label: productSubcategories[item.category]?.find(
                    sub => sub.value === item.subCategory
                )?.label ?? item.subCategory,
            },
            primaryWarranty: item.warrantyDurationInDays || 0,
            extendedWarranty: extendedWarranty,
            warrantyStatus: calculateWarrantyStatus(item.purchaseDate, item.warrantyDurationInDays+extendedWarranty),
            waarantyValidDate: getWarrantyEndDate(item.purchaseDate, item.warrantyDurationInDays+extendedWarranty),
        };
    });
};

export const formatComplaintsData = (
    data,
    serviceRequestCategories,
    serviceRequestSubcategories,
    isLastUpdatedDateInSeconds = false
) => {

    const getSubCategoryLabel = (subCategoryId) => {
        for (const subcats of Object.values(serviceRequestSubcategories)) {
            const match = subcats.find(sub => sub.value === subCategoryId);
            if (match) return match.label;
        }
        return subCategoryId;
    };

    return data.map((item, index) => {

        const lastUpdatedDate = isLastUpdatedDateInSeconds
            ? Number(item.lastUpdatedDate) * 1000
            : Number(item.lastUpdatedDate);

        // Parse customMetadata to extract complaintRefNo
        let complaintRefNo = '-';
        if (item.customMetadata) {
            try {
                const metadata = typeof item.customMetadata === 'string' 
                    ? JSON.parse(item.customMetadata) 
                    : item.customMetadata;
                complaintRefNo = metadata.complaintRefNo || '-';
            } catch (e) {
                console.error('Error parsing customMetadata:', e);
            }
        }

        return {
            ...item,
            title: item.title,
            serialNo: item.productSerial,
            complaintId: item.id,
            customerEmail: item.customerEmail,
            customerContact: item.customerContact,
            rawStatus: item.status,
            complaintReferenceNumber: item.complaintReferenceNumber || complaintRefNo,
            createdDate: toPostgresTimestamp(Number(item.createdDate)),
            lastUpdatedDate: toPostgresTimestamp(lastUpdatedDate),
            complaintCategory: serviceRequestCategories.find((cat) => cat.value === item.complaintCategory)?.label || item.complaintCategory,
            complaintCategoryObject: {
                value: item.complaintCategory,
                label: serviceRequestCategories.find((cat) => cat.value === item.complaintCategory)?.label || item.complaintCategory,
            },
            complaintSubCategory: getSubCategoryLabel(item.complaintSubCategory),
            complaintSubCategoryObject: {
                value: item.complaintSubCategory,
                label: serviceRequestSubcategories[item.complaintCategory]?.find((subCat) => subCat.value === item.complaintSubCategory)?.label || item.complaintSubCategory,
            },
            status: item.status
        };
    });
};

export const formatComplaintProductData = (
    productData,
    productCategories = [],
    productSubcategories = {},
    models = []
) => {
    if (!productData) return null;

    // Handle both nested { product: {...} } and direct product data
    const product = productData.product || productData;

    if (!product) return null;

    const modelObj = models.find(model => model.value === product.skuId);
    const categoryLabel = productCategories.find(cat => cat.value === product.category)?.label ?? product.category ?? 'Not Available';
    const subCategoryLabel = productSubcategories[product.category]?.find(
        sub => sub.value === product.subCategory
    )?.label ?? product.subCategory ?? 'Not Available';

    // Combine images from both productImages and popImages
    // Filter out images with empty URLs
    const validProductImages = (product.productImages || [])
        .filter(img => img.url && img.url.trim() !== '')
        .map(img => ({
            name: img.name || 'Product Image',
            url: img.url
        }));

    const validPopImages = (product.popImages || [])
        .filter(img => img.url && img.url.trim() !== '')
        .map(img => ({
            name: img.name || 'Product Image',
            url: img.url
        }));

    const allImages = [...validProductImages, ...validPopImages];

    return {
        id: product.id,
        name: product.name || modelObj?.label || 'Product Name',
        productImages: product.productImages || [],
        images: allImages,
        description: product.description || '',
        skuId: product.skuId || '',
        purchaseDate: product.purchaseDate ? toPostgresTimestamp(product.purchaseDate) : '',
        warrantyEndDate: product.warrantyEndDate ? toPostgresTimestamp(product.warrantyEndDate) : '',
        warrantyDurationInDays: product.warrantyDurationInDays || 0,
        warrantyStatus: product.purchaseDate && product.warrantyDurationInDays 
            ? calculateWarrantyStatus(product.purchaseDate, product.warrantyDurationInDays)
            : 'Not Available',
        warrantyValidDate: product.purchaseDate && product.warrantyDurationInDays
            ? getWarrantyEndDate(product.purchaseDate, product.warrantyDurationInDays)
            : 'Not Available',
        extWarrantyDurationInDays: product.extWarrantyDurationInDays || 0,
        category: product.category || '',
        categoryName: categoryLabel,
        categoryObject: {
            value: product.category || '',
            label: categoryLabel,
        },
        subCategory: product.subCategory || '',
        subCategoryName: subCategoryLabel,
        subCategoryObject: {
            value: product.subCategory || '',
            label: subCategoryLabel,
        },
        pendingWarranty: product.pendingWarranty || 0,
        companyId: product.companyId || '',
        popImages: product.popImages || [],
        serviceRequestStatus: product.serviceRequestStatus || {},
    };
};